'use client';

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const CONSENT_KEY = 'cookie-consent';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

interface CookieConsentProps {
  onConsentChange?: (accepted: boolean) => void;
}

export default function CookieConsent({ onConsentChange }: CookieConsentProps) {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check for existing consent
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'accepted') {
      setConsentStatus('accepted');
      onConsentChange?.(true);
    } else if (stored === 'declined') {
      setConsentStatus('declined');
      onConsentChange?.(false);
    } else {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [onConsentChange]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsentStatus('accepted');
    setIsVisible(false);
    onConsentChange?.(true);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setConsentStatus('declined');
    setIsVisible(false);
    onConsentChange?.(false);
  };

  // Don't render if consent already given or declined
  if (consentStatus !== 'pending' || !isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 animate-in slide-in-from-bottom duration-300">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.1)] border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          {/* Text Content */}
          <div className="flex-1">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
              We value your privacy
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">
              We use cookies to analyze site traffic and improve your experience.
              By clicking "Accept", you consent to our use of analytics cookies.{' '}
              <a
                href="/privacy-policy"
                className="text-soft-teal hover:underline"
              >
                Learn more
              </a>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={handleDecline}
              className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold text-white bg-soft-teal hover:bg-soft-teal-dark rounded-full transition-colors shadow-sm"
            >
              Accept
            </button>
          </div>

          {/* Close button (mobile) */}
          <button
            onClick={handleDecline}
            className="absolute top-3 right-3 sm:hidden p-1 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Export helper to check consent status
export function getConsentStatus(): ConsentStatus {
  if (typeof window === 'undefined') return 'pending';
  const stored = localStorage.getItem(CONSENT_KEY);
  if (stored === 'accepted') return 'accepted';
  if (stored === 'declined') return 'declined';
  return 'pending';
}

// Export helper to check if analytics is allowed
export function isAnalyticsAllowed(): boolean {
  return getConsentStatus() === 'accepted';
}
